import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Tab,
  Tabs,
  Modal,
  Dropdown
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminLogsAPI, bookingOrdersAPI, driversVehiclesAPI } from '../../api/apiClient';
import { useAuth } from "../../context/AuthContext";
import { useSearchFilter } from '../useSearchFilter';
import "../../assets/css/OrderDetails.css";
import Swal from 'sweetalert2';
import { 
  FaFacebookMessenger, 
  FaEye, 
  FaEyeSlash, 
  FaEuroSign, 
  FaCheck, 
  FaTimes, 
  FaBolt, 
  FaTrashAlt, 
  FaArrowLeft, 
  FaMoneyBillWave, 
  FaCog, 
  FaFileInvoice, 
  FaTruck, 
  FaSearch, 
  FaInfoCircle,
  FaMapMarkerAlt,
  FaCalendar,
  FaTruckLoading,
  FaTruckMoving,
  FaBox,
  FaUser,
  FaPhone,
  FaRoad,
  FaWeightHanging,
  FaClock,
  FaHistory,
  FaCar,
  FaList,
  FaLocationArrow
} from "react-icons/fa";

export default function BookingOrderDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  const highlightId = location.state?.highlightId;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingOrder, setBookingOrder] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [updating, setUpdating] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // States for new features
  const [price, setPrice] = useState(0);
  const [vehicleData, setVehicleData] = useState({
    vehicle_type: '',
    vehicle_number: '',
    vehicle_driver_name: '',
    phone_number: ''
  });
  const [receiveDate, setReceiveDate] = useState('');
  const [completeDate, setCompleteDate] = useState('');
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  
  // New states for driver vehicles
  const [driverVehicles, setDriverVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    fetchBookingOrderDetails();
  }, [id]);

  const fetchBookingOrderDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bookingOrdersAPI.show(id);
      
      if (response.success) {
        const data = response.data.data || response.data;
        setBookingOrder(data);
        setPrice(data.price || 0);
        setVehicleData({
          vehicle_type: data.vehicle_type || '',
          vehicle_number: data.vehicle_number || '',
          vehicle_driver_name: data.vehicle_driver_name || '',
          phone_number: data.phone_number || ''
        });
        setReceiveDate(data.received_date || '');
        setCompleteDate(data.completed_date || '');
        
        // Fetch driver vehicles if driver exists
        if (data.customer_id) {
          fetchDriverVehicles(data.customer_id);
        }
      } else {
        setError(response.message || t('failed_to_fetch_booking_details'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_booking_details'));
      console.error('Error fetching booking details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverVehicles = async (driverId) => {
    try {
      setLoadingVehicles(true);
      const response = await driversVehiclesAPI.getByDriver(driverId);
      
      if (response.success) {
        const vehicles = response.data.data || response.data;
        setDriverVehicles(vehicles);
        
        // Auto-select current vehicle if exists
        if (vehicles.length > 0 && bookingOrder?.vehicle_number) {
          const currentVehicle = vehicles.find(
            vehicle => vehicle.vehicle_number === bookingOrder.vehicle_number
          );
          if (currentVehicle) {
            setSelectedVehicle(currentVehicle);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching driver vehicles:', err);
    } finally {
      setLoadingVehicles(false);
    }
  };

  // Handle vehicle selection from dropdown
  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setVehicleData({
      vehicle_type: vehicle.vehicle_type || '',
      vehicle_number: vehicle.vehicle_number || '',
      vehicle_driver_name: vehicle.driver_name || bookingOrder?.driver_name || '',
      phone_number: vehicle.driver_phone || bookingOrder?.phone_number || ''
    });
  };

  // 4. تحديث السعر
  const handleUpdatePrice = async () => {
    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.updatePrice(id, { price: price });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.price_updated_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        fetchBookingOrderDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_price"),
        timer: 2000,
      });
    } finally {
      setUpdating(false);
    }
  };

  // 4. تحديث بيانات المركبة
  const handleUpdateVehicle = async () => {
    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.updateVehicle(id, bookingOrder.customer_id, vehicleData);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.vehicle_updated_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowVehicleModal(false);
        fetchBookingOrderDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_vehicle"),
        timer: 2000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const getMinDateTime = (baseDate = new Date()) => {
    // بنضيف دقيقة واحدة على الأقل لتجنب اختيار اللحظة الحالية بالضبط
    const adjustedDate = new Date(baseDate.getTime() + 60 * 1000);
    const isoString = adjustedDate.toISOString();
    return isoString.slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  // 5. استلام الطلب
  const handleReceiveOrder = async () => {
    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.receive(id, { received_date: receiveDate });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.order_received_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowReceiveModal(false);
        fetchBookingOrderDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_receive_order"),
        timer: 2000,
      });
    } finally {
      setUpdating(false);
    }
  };

  // 5. إكمال الطلب
  const handleCompleteOrder = async () => {
    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.complete(id, { completed_date: completeDate });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.order_completed_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowCompleteModal(false);
        fetchBookingOrderDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_complete_order"),
        timer: 2000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAccept = async () => {
    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.accept(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.order_accepted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        fetchBookingOrderDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_accept_order"),
        timer: 2000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("warning"),
        text: t("booking.please_enter_reject_reason"),
        timer: 2000,
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await bookingOrdersAPI.reject(id, { rejected_reason: rejectReason });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.rejected_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowRejectModal(false);
        setRejectReason('');
        fetchBookingOrderDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_reject"),
        timer: 2000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteBookingOrder = async () => {
      const result = await Swal.fire({
        title: t('are_you_sure'),
        text: t('confirm_delete_order'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('delete'),
        cancelButtonText: t('cancel')
      });
  
      if (result.isConfirmed) {
        try {
          const response = await bookingOrdersAPI.delete(id);
          
          if (response.success) {
            Swal.fire({
              icon: 'success',
              title: t('deleted'),
              text: t('order_deleted_successfully'),
              timer: 2000,
              showConfirmButton: false
            });
            navigate('/admin/booking-orders');
          } else {
            throw new Error(response.message);
          }
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: t('error'),
            text: err.message || t('failed_to_delete_order'),
            timer: 2000,
          });
        }
      }
    };

  const handleViewLiveTracking = (id) => {
    navigate(`/admin/bookings/live-tracking/${id}`);
  };

  const handleBack = () => {
    const state = {
      fromPage: location.state?.fromPage || 1,
      currentPage: location.state?.currentPage || 1,
      highlightId: location.state?.highlightId || parseInt(id),
      scrollToHighlight: true,
      activeTab: location.state?.activeTab || 'active'
    };
    
    console.log('Navigating back with state:', state);
    
    navigate('/admin/booking-orders', { 
      state: state
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      cancelled: { variant: 'danger', text: t('cancelled') },
      rejected: { variant: 'danger', text: t('rejected') },
      assigned: { variant: 'info', text: t('assigned') },
      received: { variant: 'secondary', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'warning', text: t('pending') },
      approved: { variant: 'success', text: t('approved') }
    };
  
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
        <span className="ms-2">{t('loading')}</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="danger" className="m-3">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!bookingOrder) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="warning" className="m-3">
          {t('booking_not_found')}
          <Button variant="outline-warning" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-1 bg-light min-vh-100 booking-order-details">
      {/* Header Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <FaTruck className="text-primary me-3" size={32} />
                <div>
                  <h2 className="mb-1 fw-bold">
                    {t("booking.order_details")} - <span className="text-primary">{bookingOrder.order_id}</span>
                  </h2>
                  <p className="text-muted mb-0">
                    {t("booking.booking_id")}: 
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none"
                      onClick={() => navigate(`/admin/bookings/details/${bookingOrder.booking?.id}`)}
                    >
                      {bookingOrder.booking?.booking_id}
                    </Button>
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <div className="d-flex flex-column align-items-end">
                <div className="mb-2">
                  {getStatusBadge(bookingOrder.status)}

                  {bookingOrder.booking?.fast_order == 1 && (
                    <Badge bg={bookingOrder.booking?.fast_order ? 'success' : 'warning'} className="ms-2">
                      {t('booking.fast_booking')}
                    </Badge>
                  )}
                </div>
                <small className="text-muted">
                  {t('booking.created_on')}: {formatDateTime(bookingOrder.created_at)}
                </small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Quick Actions Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title">
          <h5 className="mb-0 fw-bold">
            <FaCog className="me-2 text-primary" />
            {t("booking.actions_section")}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            {bookingOrder.status === 'pending' && (
              <Col md={4} className="mb-3">
                <div className="d-grid gap-2">
                    <Button 
                      variant="outline-primary" 
                      onClick={() => setShowVehicleModal(true)}
                      disabled={updating}
                    >
                      <FaTruck className="me-2" />
                      {t("update_vehicle")}
                    </Button>
                </div>
              </Col>
            )}
            <Col md={4} className="mb-3">
              <div className="d-grid gap-2">
                {bookingOrder && (bookingOrder.status !== 'completed' && bookingOrder.status !== 'pending') && (
                  <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewLiveTracking(bookingOrder.id)}
                  className="mx-1"
                  >
                  <FaLocationArrow /> {t("live_tracking")}
                  </Button>
                )}
                {bookingOrder.status === 'assigned' && (
                  <Button 
                    variant="outline-warning" 
                    onClick={() => setShowReceiveModal(true)}
                    disabled={updating}
                  >
                    <FaTruckLoading className="me-2" />
                    {t("booking.mark_received")}
                  </Button>
                )}
                {bookingOrder.received_date && !bookingOrder.completed_date && (
                  <Button 
                    variant="outline-info" 
                    onClick={() => setShowCompleteModal(true)}
                    disabled={updating}
                  >
                    <FaTruckMoving className="me-2" />
                    {t("booking.mark_completed")}
                  </Button>
                )}
              </div>
            </Col>
            <Col md={4} className="mb-3">
              <div className="d-grid gap-2">
                {bookingOrder.status === 'pending' && (
                  <>
                    <Button 
                      variant="success" 
                      onClick={handleAccept}
                      disabled={updating}
                    >
                      <FaCheck className="me-2" />
                      {t("accept_order")}
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={() => setShowRejectModal(true)}
                      disabled={updating}
                    >
                      <FaTimes className="me-2" />
                      {t("reject_order")}
                    </Button>
                  </>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mt-3 pt-3 border-top">
            <Col className="d-flex flex-wrap gap-2 justify-content-between">
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={handleBack} size="sm">
                  <FaArrowLeft className="me-1" />
                  {t("booking.back")}
                </Button>
                
                <Button 
                  variant="outline-danger" 
                  onClick={handleDeleteBookingOrder}
                  size="sm"
                >
                  <FaTrashAlt className="me-1" />
                  {t("delete")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" fill>
        {/* Details Tab */}
        <Tab eventKey="details" title={t("booking_details")}>
          <Row>
            {/* Left Column */}
            <Col md={8}>
              {/* Route Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaRoad className="me-2 text-primary" />
                    {t("booking.route_information")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="loading-info p-3 border rounded bg-light">
                        <h6 className="fw-bold text-success mb-3">
                          <FaMapMarkerAlt className="me-2" />
                          {t("booking.loading_points")} ({bookingOrder.booking?.booking_from?.length || 0})
                        </h6>
                        {bookingOrder.booking?.booking_from?.map((address, index) => (
                          <div key={index} className="mb-3">
                            <p className="fw-bold mb-1">{t('loading_location')} {index + 1}</p>
                            <p className="mb-1">{address}</p>
                            <small className="text-muted">
                              {t("booking.loading_date")}: {bookingOrder.booking?.loading_date_time?.[index]}
                            </small>
                          </div>
                        ))}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="unloading-info p-3 border rounded bg-light">
                        <h6 className="fw-bold text-danger mb-3">
                          <FaMapMarkerAlt className="me-2" />
                          {t("booking.unloading_points")} ({bookingOrder.booking?.booking_to?.length || 0})
                        </h6>
                        {bookingOrder.booking?.booking_to?.map((address, index) => (
                          <div key={index} className="mb-3">
                            <p className="fw-bold mb-1">{t('unloading_location')} {index + 1}</p>
                            <p className="mb-1">{address}</p>
                            <small className="text-muted">
                              {t("booking.unloading_date")}: {bookingOrder.booking?.unloading_date_time?.[index]}
                            </small>
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Route Summary */}
                  <Row className="mt-3">
                    <Col md={6}>
                      <div className="text-center p-2 border rounded">
                        <FaRoad className="text-info mb-2" size={24} />
                        <h6 className="mb-1">{t("booking.distance")}</h6>
                        <p className="fw-bold mb-0">{bookingOrder.booking?.distance}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="text-center p-2 border rounded">
                        <FaWeightHanging className="text-warning mb-2" size={24} />
                        <h6 className="mb-1">{t("booking.total_weight")}</h6>
                        <p className="fw-bold mb-0">{bookingOrder.booking?.total_weight}</p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Driver & Vehicle Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaTruck className="me-2 text-success" />
                    {t("booking.driver_vehicle_info")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="driver-info p-3 border rounded bg-light">
                        <h6 className="fw-bold mb-3">
                          <FaUser className="me-2" />
                          {t("driver_information")}
                        </h6>
                        <p><strong>{t("driver_name")}:</strong> {bookingOrder.driver_name}</p>
                        <p><strong>{t("email")}:</strong> {bookingOrder.driver_email}</p>
                        <p><strong>{t("phone")}:</strong> {bookingOrder.phone_number}</p>
                        <p><strong>{t("role")}:</strong> {bookingOrder.driver_role}</p>
                        <p><strong>{t("current_location")}:</strong> {bookingOrder.current_location}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="vehicle-info p-3 border rounded bg-light">
                        <h6 className="fw-bold mb-3">
                          <FaTruck className="me-2" />
                          {t("booking.vehicle_information")}
                        </h6>
                        <p><strong>{t("vehicle_type")}:</strong> {bookingOrder.vehicle_type}</p>
                        <p><strong>{t("vehicle_number")}:</strong> {bookingOrder.vehicle_number}</p>
                        <p><strong>{t("vehicle_driver_name")}:</strong> {bookingOrder.vehicle_driver_name}</p>
                        <p><strong>{t("booking.price")}:</strong> {bookingOrder.price} EUR</p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column */}
            <Col md={4}>
              {/* Order Timeline */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  <h5 className="mb-0 fw-bold">
                    <FaHistory className="me-2 text-info" />
                    {t("booking.order_timeline")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-marker bg-success"></div>
                      <div className="timeline-content">
                        <h6 className="fw-bold">{t("booking.created_on")}</h6>
                        <p className="text-muted mb-0">{bookingOrder.created_at}</p>
                      </div>
                    </div>
                    
                    {bookingOrder.assign_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-info"></div>
                        <div className="timeline-content">
                          <h6 className="fw-bold">{t("booking.assigned_on")}</h6>
                          <p className="text-muted mb-0">{bookingOrder.assign_date}</p>
                        </div>
                      </div>
                    )}
                    
                    {bookingOrder.received_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-warning"></div>
                        <div className="timeline-content">
                          <h6 className="fw-bold">{t("booking.received_on")}</h6>
                          <p className="text-muted mb-0">{bookingOrder.received_date}</p>
                        </div>
                      </div>
                    )}
                    
                    {bookingOrder.completed_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-primary"></div>
                        <div className="timeline-content">
                          <h6 className="fw-bold">{t("booking.completed_on")}</h6>
                          <p className="text-muted mb-0">{bookingOrder.completed_date}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>

            {bookingOrder.status === 'pending' && (
                <>
                  {/* Price Information */}
                  <Card className="mb-4 shadow-sm">
                    <Card.Header className="fw-bold card-title">
                      <h5 className="mb-0 fw-bold">
                        <FaEuroSign className="me-2 text-success" />
                        {t("booking.price_information")}
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="price-section">
                        <div className=" mb-3">
                          <div className="mb-2"><strong>{t("booking.net")}:</strong> {bookingOrder.booking.sub_total} EUR</div>
                          <div className="mb-2"><strong>{t('Gesamt Bertrag')}</strong></div>
                          <div className="mb-2"><strong>{t("exkl. 19% MwSt. Brutto")}</strong> {bookingOrder.booking.total} EUR</div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="fw-bold">{t("booking.current_price")}:</span>
                          <span className="fw-bold text-success fs-5">{bookingOrder.price} EUR</span>
                        </div>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>{t("update_price")}</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              value={price}
                              onChange={(e) => setPrice(e.target.value)}
                              min="0"
                              step="0.01"
                            />
                            <InputGroup.Text>EUR</InputGroup.Text>
                          </InputGroup>
                        </Form.Group>
                        
                        <Button 
                          variant="success" 
                          onClick={handleUpdatePrice}
                          disabled={updating}
                          className="w-100"
                        >
                          {updating ? <Spinner size="sm" /> : t("update_price")}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </>
              )}

              {/* Live Tracking */}
              {bookingOrder.liveTracking && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header className="fw-bold card-title">
                    <h5 className="mb-0 fw-bold">
                      <FaMapMarkerAlt className="me-2 text-danger" />
                      {t("live_tracking")}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="tracking-info">
                      <p><strong>{t("current_location")}:</strong></p>
                      <p className="mb-1">Lat: {bookingOrder.liveTracking.lat}</p>
                      <p className="mb-1">Lng: {bookingOrder.liveTracking.lng}</p>
                      <small className="text-muted">
                        {t("last_updated")}: {bookingOrder.liveTracking.recorded_at}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        {/* Logs Tab */}
        <Tab eventKey="logs" title={t("admin_logs")}>
          <Card className="shadow-sm">
            <Card.Header className="fw-bold card-title">
              <h5 className="mb-0 fw-bold">
                <FaHistory className="me-2" />
                {t("logs")}
              </h5>
            </Card.Header>
            <Card.Body>
              {bookingOrder.admin_logs && bookingOrder.admin_logs.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-dark">
                      <tr>
                        <th>{t("admin_name")}</th>
                        <th>{t("admin_email")}</th>
                        <th>{t("action")}</th>
                        <th>{t("description")}</th>
                        <th>{t("created_at")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingOrder.admin_logs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.admin_name || "N/A"}</td>
                          <td>{log.admin_email || "N/A"}</td>
                          <td>{log.action}</td>
                          <td>{log.description}</td>
                          <td>{log.created_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FaHistory size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">{t("no_logs_found")}</h5>
                  <p className="text-muted">{t("no_activity_logs")}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Modals */}
      
      {/* Update Vehicle Modal */}
      <Modal show={showVehicleModal} onHide={() => setShowVehicleModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTruck className="me-2" />
            {t("booking.update_vehicle_info")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Driver Vehicles Dropdown */}
          {driverVehicles.length > 0 && (
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">
                <FaList className="me-2" />
                {t("booking.select_from_driver_vehicles")}
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle variant="outline-primary" className="w-100">
                  {selectedVehicle ? (
                    <>
                      <FaCar className="me-2" />
                      {selectedVehicle.vehicle_type} - {selectedVehicle.vehicle_number}
                    </>
                  ) : (
                    t("booking.select_vehicle")
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {driverVehicles.map((vehicle) => (
                    <Dropdown.Item 
                      key={vehicle.id} 
                      onClick={() => handleVehicleSelect(vehicle)}
                      className={selectedVehicle?.id === vehicle.id ? 'bg-light' : ''}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{vehicle.vehicle_type}</strong>
                          <br />
                          <small className="text-muted">
                            {vehicle.vehicle_number} • {vehicle.driver_name}
                          </small>
                        </div>
                        {selectedVehicle?.id === vehicle.id && (
                          <FaCheck className="text-success" />
                        )}
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Form.Text className="text-muted">
                {t("booking.select_vehicle_help")}
              </Form.Text>
            </Form.Group>
          )}

          <div className="border-top pt-3">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("vehicle_type")} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={vehicleData.vehicle_type}
                      onChange={(e) => setVehicleData({...vehicleData, vehicle_type: e.target.value})}
                      placeholder={t("booking.enter_vehicle_type")}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("vehicle_number")} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={vehicleData.vehicle_number}
                      onChange={(e) => setVehicleData({...vehicleData, vehicle_number: e.target.value})}
                      placeholder={t("booking.enter_vehicle_number")}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("vehicle_driver_name")} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={vehicleData.vehicle_driver_name}
                      onChange={(e) => setVehicleData({...vehicleData, vehicle_driver_name: e.target.value})}
                      placeholder={t("booking.enter_driver_name")}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("phone_number")} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={vehicleData.phone_number}
                      onChange={(e) => setVehicleData({...vehicleData, phone_number: e.target.value})}
                      placeholder={t("booking.enter_phone_number")}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>

          {/* Current Selection Preview */}
          {selectedVehicle && (
            <Card className="mt-3 border-success">
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 text-success">
                      <FaCheck className="me-2" />
                      {t("booking.selected_vehicle")}
                    </h6>
                    <small>
                      {selectedVehicle.vehicle_type} • {selectedVehicle.vehicle_number} • {selectedVehicle.driver_name}
                    </small>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSelectedVehicle(null);
                      setVehicleData({
                        vehicle_type: '',
                        vehicle_number: '',
                        vehicle_driver_name: '',
                        phone_number: ''
                      });
                    }}
                  >
                    <FaTimes />
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVehicleModal(false)}>
            {t("cancel")}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdateVehicle} 
            disabled={updating || !vehicleData.vehicle_type || !vehicleData.vehicle_number}
          >
            {updating ? <Spinner size="sm" /> : t("booking.update_vehicle")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Receive Order Modal */}
      <Modal show={showReceiveModal} onHide={() => setShowReceiveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTruckLoading className="me-2" />
            {t("booking.mark_as_received")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("booking.receive_date")} *</Form.Label>
            <Form.Control
              type="datetime-local"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              min={getMinDateTime()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReceiveModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="warning" onClick={handleReceiveOrder} disabled={updating}>
            {updating ? <Spinner size="sm" /> : t("booking.mark_received")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Complete Order Modal */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTruckMoving className="me-2" />
            {t("booking.mark_as_completed")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">{t("booking.receive_date")}: {receiveDate}</div>
          <Form.Group>
            <Form.Label>{t("booking.complete_date")} *</Form.Label>
            <Form.Control
              type="datetime-local"
              value={completeDate}
              onChange={(e) => setCompleteDate(e.target.value)}
              min={receiveDate ? receiveDate : getMinDateTime()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleCompleteOrder} disabled={updating}>
            {updating ? <Spinner size="sm" /> : t("booking.mark_completed")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("booking.reject_order")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("booking.reject_reason")} *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("booking.enter_reject_reason")}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={updating}>
            {updating ? <Spinner size="sm" /> : t("booking.confirm_reject")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}